import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';

const TOKEN = document.querySelector("[name='csrf-token']")?.content;

export default function FormatFilter({ value, operator, onChange, columnId }) {
  const [op, setOp] = useState(operator || 'contiene');

  useEffect(() => { setOp(operator || 'contiene'); }, [operator]);

  // Multi-select: value is comma-separated "surveyId_qId,surveyId_qId,..."
  // selectedItems: [{ value: "5832_12456", label: "Juan Pérez" }, ...]
  const [selectedItems, setSelectedItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 200 });
  const anchorRef = useRef(null);
  const debounceRef = useRef(null);

  // Sync selectedItems when value is cleared externally
  useEffect(() => { if (!value) setSelectedItems([]); }, [value]);

  const fetchOptions = useCallback(async (term) => {
    setLoading(true);
    try {
      const res = await fetch(
        `/formatos/get_formats_values_format/${columnId}/${encodeURIComponent(term)}/0/0`,
        { headers: { 'X-CSRF-Token': TOKEN, 'Content-Type': 'application/json' } }
      );
      const json = await res.json();
      setOptions(json.data || []);
    } catch (err) {
      console.error('FormatFilter fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [columnId]);

  const handleOpen = () => {
    if (anchorRef.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 2, left: rect.left, width: Math.max(rect.width, 280) });
    }
    setOpen(true);
    setSearch('');
    fetchOptions('bw-blank');
  };

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (val.length >= 3) {
      debounceRef.current = setTimeout(() => fetchOptions(val), 300);
    } else if (val.length === 0) {
      debounceRef.current = setTimeout(() => fetchOptions('bw-blank'), 300);
    }
  };

  const isSelected = (optValue) => selectedItems.some(s => s.value === optValue);

  const handleToggleItem = (opt) => {
    let next;
    if (isSelected(opt.value)) {
      next = selectedItems.filter(s => s.value !== opt.value);
    } else {
      next = [...selectedItems, { value: opt.value, label: opt.label }];
    }
    setSelectedItems(next);
    const combinedValue = next.map(s => s.value).join('|||');
    onChange(combinedValue, op);
  };

  const handleClear = () => {
    setSelectedItems([]);
    onChange('', op);
    setOpen(false);
  };

  const handleOpChange = (e) => {
    setOp(e.target.value);
    const combinedValue = selectedItems.map(s => s.value).join('|||');
    onChange(combinedValue, e.target.value);
  };

  const displayLabel = selectedItems.length === 0
    ? null
    : selectedItems.length === 1
      ? selectedItems[0].label
      : `${selectedItems.length} seleccionados`;

  const dropdown = open ? ReactDOM.createPortal(
    <>
      <div className="cm-menu-backdrop" onClick={() => setOpen(false)} />
      <div className="cm-format-filter-dropdown" style={{ top: pos.top, left: pos.left, width: pos.width }}>
        <div className="cm-format-filter-dropdown__search">
          <i className="fas fa-search" style={{ color: '#9ca3af', fontSize: 11 }} />
          <input
            type="text"
            placeholder="Buscar (min. 3)..."
            value={search}
            onChange={handleSearchChange}
            autoFocus
          />
        </div>

        {selectedItems.length > 0 && (
          <div style={{ padding: '4px 8px', display: 'flex', flexWrap: 'wrap', gap: 4, borderBottom: '1px solid #e5e7eb' }}>
            {selectedItems.map((item, i) => (
              <span key={i} style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: '2px 8px', fontSize: 11, background: '#e8f0fe',
                borderRadius: 10, color: '#1a73e8', maxWidth: '100%'
              }}>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.label}</span>
                <i className="fas fa-times" style={{ cursor: 'pointer', fontSize: 9 }}
                  onClick={(e) => { e.stopPropagation(); handleToggleItem(item); }} />
              </span>
            ))}
          </div>
        )}

        <div className="cm-format-filter-dropdown__list">
          {loading ? (
            <div className="cm-format-filter-dropdown__msg">
              <i className="fas fa-spinner fa-spin" /> Buscando...
            </div>
          ) : options.length === 0 ? (
            <div className="cm-format-filter-dropdown__msg">Sin resultados</div>
          ) : (
            options.map((opt, i) => (
              <div
                key={i}
                className={`cm-format-filter-dropdown__option${isSelected(opt.value) ? ' cm-format-filter-dropdown__option--active' : ''}`}
                onClick={() => handleToggleItem(opt)}
              >
                <input
                  type="checkbox"
                  checked={isSelected(opt.value)}
                  readOnly
                  style={{ marginRight: 6, width: 14, height: 14, cursor: 'pointer' }}
                />
                {opt.label}
              </div>
            ))
          )}
        </div>
        {selectedItems.length > 0 && (
          <div className="cm-format-filter-dropdown__clear" onClick={handleClear}>
            <i className="fas fa-eraser" /> Limpiar todo
          </div>
        )}
      </div>
    </>,
    document.body
  ) : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <div
        ref={anchorRef}
        className="cm-filter-input cm-format-filter-trigger"
        onClick={(e) => { e.stopPropagation(); handleOpen(); }}
      >
        {displayLabel || <span style={{ color: '#b0b5bd' }}>Buscar...</span>}
      </div>
      <select className="cm-filter-select" value={op} onChange={handleOpChange}>
        <option value="contiene">Contiene</option>
        <option value="igual">Igual</option>
        <option value="diferente">Diferente</option>
      </select>
      {dropdown}
    </div>
  );
}
